import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { FilterBar } from "@/components/shared/PageChrome";
import { StackedList, Pagination } from "@/components/shared/StackedList";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared/States";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BoroughSelect, useBoroughOptions } from "@/components/shared/BoroughSelect";
import { useAuth } from "@/contexts/AuthContext";

const STATUSES = ["CONFIRMED", "AMENDED", "PARTIALLY_CANCELLED", "CANCELLED", "COMPLETED"];

export default function BookingsList() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  const status = params.get("status") || "ALL";
  const q = params.get("q") || "";
  const borough = params.get("borough") || "";
  const { hasWorkspace } = useAuth();
  const isStaff = hasWorkspace("INTERNAL");
  const boroughOptions = useBoroughOptions();

  const PAGE_SIZE = 25;
  const rawPage = params.get("page");
  const page = Math.max(1, parseInt(rawPage || "1", 10) || 1);

  useEffect(() => {
    const query = { page, page_size: PAGE_SIZE };
    if (status !== "ALL") query.status = status;
    if (q) query.q = q;
    if (borough) query.borough = borough;
    api.get("/bookings", { params: query }).then((r) => {
      setData(r.data);
      if (r.data.page !== page || (rawPage !== null && String(r.data.page) !== rawPage)) { const next = new URLSearchParams(params); if (r.data.page <= 1) next.delete("page"); else next.set("page", String(r.data.page)); setParams(next, { replace: true }); } // server clamped an invalid page
    }).catch(() => setError(true));
  }, [status, q, page, borough]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (mutate) => { const next = new URLSearchParams(params); mutate(next); setParams(next); };
  const setStatus = (v) => update((n) => { if (v === "ALL") n.delete("status"); else n.set("status", v); n.delete("page"); });
  const setQ = (v) => update((n) => { if (!v) n.delete("q"); else n.set("q", v); n.delete("page"); });
  const setBorough = (v) => update((n) => { if (!v) n.delete("borough"); else n.set("borough", v); n.delete("page"); });
  const setPage = (p) => update((n) => { if (p <= 1) n.delete("page"); else n.set("page", String(p)); });

  return (
    <div data-testid="bookings-list-page" className="space-y-5">
      <div>
        <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.01em] text-jigsaw-navy">Bookings</h1>
        <p className="text-sm text-jigsaw-muted">Confirmed accommodation bookings across all clients.</p>
      </div>

      <FilterBar testId="bookings-filter-bar" summary={data ? `${data.total} bookings match` : null}>
        <Input data-testid="bookings-search-input" placeholder="Filter by booking reference..." defaultValue={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-full sm:max-w-xs" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="bookings-status-filter" className="h-9 w-full sm:w-52"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        {isStaff && <BoroughSelect id="bookings-borough-filter" label={null} value={borough} onChange={setBorough} options={boroughOptions} allLabel="All boroughs" allowNotRecordedFilter className="h-9 w-full sm:w-56" />}
      </FilterBar>

      {error && <ErrorState testId="bookings-error" />}
      {!data && !error && <LoadingState testId="bookings-loading" />}
      {data && data.items.length === 0 && <EmptyState testId="bookings-empty" title="No bookings match these filters" />}

      {data && data.items.length > 0 && (
        <div className="space-y-3">
          <StackedList testId="bookings-stacked" items={data.items} title={(b) => `${b.booking_reference} · ${b.client_name}`} status={(b) => <StatusBadge status={b.status} />} openLabel="Open booking"
            onOpen={(b) => navigate(`/control-centre/bookings/${b.id}`)}
            fields={[{ label: "Dates", value: (b) => `${b.check_in_date || "—"} → ${b.check_out_date || "—"}`, mono: true, full: true },
              { label: "Rooms / guests", value: (b) => `${b.total_rooms ?? "—"} / ${b.total_guests ?? "—"}` }, { label: "Value", value: (b) => (b.legacy && b.value_amount == null ? "Not reconciled (legacy)" : b.value_amount != null ? `£${b.value_amount.toFixed(2)}` : "—"), mono: true }]} />
          <div className="hidden sm:block">
          <Table data-testid="bookings-table">
            <TableHeader>
              <TableRow>
                <TableHead>Reference</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Stay dates</TableHead>
                <TableHead>Guests</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((b) => (
                <TableRow key={b.id} data-testid={`booking-row-${b.booking_reference}`} className="cursor-pointer" onClick={() => navigate(`/control-centre/bookings/${b.id}`)}>
                  <TableCell className="font-mono-ref font-semibold text-jigsaw-navy">{b.booking_reference}</TableCell>
                  <TableCell className="font-medium">{b.client_name}{b.legacy && b.client_contact ? <span className="block text-xs font-normal text-jigsaw-muted">{b.client_contact}</span> : null}{b.legacy && <span className="ml-2 rounded-full border border-jigsaw-border px-1.5 py-0.5 text-[10px] text-jigsaw-muted">{b.migration_hold ? "legacy · hold" : "legacy"}</span>}</TableCell>
                  <TableCell className="font-mono-ref whitespace-nowrap text-[13px]">{b.check_in_date || "—"} &rarr; {b.check_out_date || "—"}{isStaff && b.borough_label && <span className="block font-sans text-[11px] text-jigsaw-muted" data-testid={`booking-borough-${b.id}`}>{b.borough_label}</span>}</TableCell>
                  <TableCell>{b.legacy ? <span data-testid={`booking-guests-${b.id}`}><span className="text-xs text-jigsaw-muted">{b.stay_entries} stay {b.stay_entries === 1 ? "entry" : "entries"}{b.guest_entries != null ? ` · ${b.guest_entries} guest entries` : ""}</span>{b.guest_names?.length ? <span className="block text-xs text-jigsaw-muted">{b.guest_names.join(", ")}{b.guest_names_more ? ` +${b.guest_names_more} more` : ""}</span> : null}</span> : b.total_guests}</TableCell>
                  <TableCell className="font-mono-ref text-right">{b.value_amount != null ? `£${b.value_amount.toFixed(2)}` : (b.legacy ? <span className="text-xs text-jigsaw-muted" title="Source rates and payments are kept on the booking record; no reconciled modern total exists">Not reconciled (legacy)</span> : "—")}</TableCell>
                  <TableCell><StatusBadge status={b.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <Pagination testId="bookings-pagination" page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />
        </div>
      )}
    </div>
  );
}
